import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICustomer,
  ICustomerDetail,
  IDropdownCustomer,
  CustomerState,
  ICustomerForm,
} from '../../../types/customer/customerInterfaces';

export const initialFormState: ICustomerForm = {
  id: null,
  name: '',
  nit: '',
  nrc: '',
  dui: '',
  passportNumber: '',
  residentCardNumber: '',
  otherIdentification: '',
  legalName: '',
  tradeName: '',
  phone: '',
  email: '',
  complement: '',
  entityTypeId: null,
  taxpayerCategoryId: null,
  economicActivityId: null,
  departmentId: null,
  municipalityId: null,
  documentIdentificationTypeId: null,
  documentIdentificationTypeCode: '',
  status: true,
  defaultCustomer: false,
};

const initialState: CustomerState = {
  form: initialFormState,
  detail: null,
  list: [],
  paginator: null,
  isLoading: false,
  isSubmitting: false,
  errors: null,
  customersDropdown: [],
  showRegistrationModal: false,
  showViewModal: false,
  showEditModal: false,
};

const customerConfigurationSlice = createSlice({
  name: 'customerConfiguration',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    // Guardar lista de clientes
    setCustomerList(state, action: PayloadAction<ICustomer[]>) {
      state.list = [...action.payload].sort(
        (left, right) =>
          Number(right.defaultCustomer) - Number(left.defaultCustomer)
      );
    },
    addCustomerToList(state, action: PayloadAction<ICustomer>) {
      // Agrega el nuevo cliente al inicio de la lista
      state.list = [
        action.payload,
        ...state.list.map((customer) =>
          action.payload.defaultCustomer
            ? { ...customer, defaultCustomer: false }
            : customer
        ),
      ];
    },
    setPaginator(state, action: PayloadAction<any>) {
      state.paginator = action.payload;
    },
    // Dropdown de clientes
    setCustomersDropdown(state, action: PayloadAction<IDropdownCustomer[]>) {
      state.customersDropdown = [...action.payload].sort(
        (left, right) =>
          Number(right.defaultCustomer) - Number(left.defaultCustomer)
      );
    },
    // Detalle para editar o ver
    setCustomerDetail(state, action: PayloadAction<ICustomerDetail>) {
      state.detail = action.payload;
      state.form = {
        ...initialFormState,
        id: action.payload.id ?? null,
        name: action.payload.name ?? '',
        nit: action.payload.nit ?? '',
        nrc: action.payload.nrc ?? '',
        dui: action.payload.dui ?? '',
        passportNumber: action.payload.passportNumber ?? '',
        residentCardNumber: action.payload.residentCardNumber ?? '',
        otherIdentification: action.payload.otherIdentification ?? '',
        legalName: action.payload.legalName ?? '',
        tradeName: action.payload.tradeName ?? '',
        phone: action.payload.phone ?? '',
        email: action.payload.email ?? '',
        complement: action.payload.complement ?? '',
        entityTypeId: action.payload.entityTypeId ?? 0,
        documentIdentificationTypeId:
          action.payload.documentIdentificationTypeId ?? null,
        status: action.payload.status ?? true,
        defaultCustomer: action.payload.defaultCustomer ?? false,
      };

      // 🔄 limpiar errores previos
      state.errors = null;
    },

    setCustomerFormForEdit(state, action: PayloadAction<ICustomerForm>) {
      const data = action.payload;

      state.form = {
        id: data.id,
        name: data.name ?? '',
        nit: data.nit ?? '',
        nrc: data.nrc ?? '',
        dui: data.dui ?? '',
        passportNumber: data.passportNumber ?? '',
        residentCardNumber: data.residentCardNumber ?? '',
        otherIdentification: data.otherIdentification ?? '',
        legalName: data.legalName ?? '',
        tradeName: data.tradeName ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
        complement: data.complement ?? '',
        entityTypeId: data.entityTypeId ?? null,
        taxpayerCategoryId: data.taxpayerCategoryId ?? null,
        economicActivityId: data.economicActivityId ?? null,
        countryId: data.countryId ?? null,
        departmentId: data.departmentId ?? null,
        departmentCode: data.departmentCode ?? '',
        municipalityId: data.municipalityId ?? null,
        documentIdentificationTypeId: data.documentIdentificationTypeId ?? null,
        documentIdentificationTypeCode:
          data.documentIdentificationTypeCode ?? '',
        status: data.status ?? true,
        defaultCustomer: data.defaultCustomer ?? false,
      };

      state.errors = null; // limpiar errores previos
    },

    // Actualizar campo del formulario
    updateField(
      state,
      action: PayloadAction<{
        field: keyof ICustomerForm;
        value: any;
      }>
    ) {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },

    // Resetear formulario
    resetCustomerForm(state) {
      state.form = initialFormState;
      state.detail = null;
      state.isSubmitting = false;
      state.errors = null;
    },
    // Control de modales
    setShowRegistrationModal(state, action: PayloadAction<boolean>) {
      state.showRegistrationModal = action.payload;
    },
    setShowViewModal(state, action: PayloadAction<boolean>) {
      state.showViewModal = action.payload;
    },
    setShowEditModal(state, action: PayloadAction<boolean>) {
      state.showEditModal = action.payload;
    },
    // Actualizar estado activo/inactivo
    updateCustomerStatusInList(
      state,
      action: PayloadAction<{ id: number; status: boolean }>
    ) {
      const { id, status } = action.payload;
      state.list = state.list.map((customer) =>
        customer.id === id ? { ...customer, status } : customer
      );
    },
    setErrors(state, action: PayloadAction<Record<string, string> | null>) {
      console.log(action.payload);
      state.errors = action.payload;
    },
    clearCustomerList(state) {
      state.list = [];
      state.paginator = null;
    },

    clearIdentificationFields(state) {
      state.form.nit = null;
      state.form.dui = null;
      state.form.passportNumber = null;
      state.form.residentCardNumber = null;
      state.form.otherIdentification = null;
    },
  },
});

export const {
  setLoading,
  setSubmitting,
  setCustomerList,
  addCustomerToList,
  setPaginator,
  setCustomersDropdown,
  setCustomerDetail,
  setCustomerFormForEdit,
  updateField,
  resetCustomerForm,
  setShowRegistrationModal,
  setShowViewModal,
  setShowEditModal,
  updateCustomerStatusInList,
  setErrors,
  clearCustomerList,
  clearIdentificationFields,
} = customerConfigurationSlice.actions;

export default customerConfigurationSlice.reducer;
